import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CameraScene, SCENE_PLATE, SCENE_PLATE_MOBILE, SceneBlock } from './CameraScene';

// The two halves of the promise, switched in one slot at the end of the second
// line. Reading them one after the other in the same position makes the point
// that they are the same decision, not two separate features.
/*
 * The full stop is NOT part of these strings.
 *
 * Everywhere else on the site the terminating dot is set in orange against ink
 * or cream type — the smallest mark on the page carrying the accent. Here the
 * closing phrase is ALREADY orange, so an orange dot would say nothing. It
 * inverts instead: the dot takes the headline's own off-white, which keeps the
 * motif intact (the stop always contrasts what it ends) and reads correctly,
 * since the stop belongs to the whole sentence rather than to the highlighted
 * phrase. It is rendered inside the animated span so it travels with the
 * phrase on the swap rather than jumping as the width changes.
 */
const PROMISES = ['go faster', 'stay in control'] as const;
const SWITCH_MS = 3400;
const SWITCH_OFFSET_MS = 2200;
export function HeroCameraScene() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduce) return;
    let interval: number | undefined;
    const timeout = window.setTimeout(() => {
      setIndex(current => (current + 1) % PROMISES.length);
      interval = window.setInterval(() => {
        setIndex(current => (current + 1) % PROMISES.length);
      }, SWITCH_MS);
    }, SWITCH_OFFSET_MS);
    return () => {
      window.clearTimeout(timeout);
      if (interval) window.clearInterval(interval);
    };
  }, [reduce]);
  // 300svh, not 220. The claim now settles at ~80% of travel and does not
  // start leaving until 94%, so that 14% window is the beat where it simply
  // sits in the middle of the shot and is read. At 220svh that window was
  // about a third of a screen of scroll — gone in one flick.
  return <CameraScene photo={SCENE_PLATE} photoMobile={SCENE_PLATE_MOBILE} travel="300svh" claimsBottomEdge statement={
  /* Bottom-left, not centred. The plate's subject is a vertical ascent
   through the middle of the frame, so centred copy sits on top of the
   rocket; anchored to the lower-left corner the type occupies the dark
   mountain mass and leaves the climb entirely unobstructed. */
  /* Same container and gutter as the site header, so the headline's left
   edge sits exactly under the Giga mark. A different max-width or padding
   here is what made it read as vaguely centred rather than anchored. */
  <div className="container mx-auto px-6" data-id="element-830" data-mp="133aw7lncijmx">
          {/* The box is sized BY the lines, not the lines by the box: each line
           reveals from inside its own overflow-hidden clip, so any width cap
           narrower than the text crops it mid-word. */}
          <SceneBlock className="w-fit max-w-full" data-id="element-831" data-mp="1vlyg4xqlvsmf">
            {/* Matches the page's section headlines in size — the opening
             statement must not be outranked by a mid-page heading. The fluid
             size still keeps each line on one row down to narrow widths. */}
            {/* The vw factor is steeper below sm, where the line is allowed to
             wrap: at the old 6.3vw a phone bottomed out on the 28px floor and
             the page's opening claim was smaller than its own body copy. */}
            {/* Below sm the line is allowed to wrap, and left to itself it broke
             after "for" — leaving "companies" alone on a line and the claim
             reading as four ragged fragments. An explicit phone-only break
             after "platform" splits it where the sense does, giving two even
             lines, which in turn frees the type to be considerably larger:
             10.6vw instead of 8.6vw. */}
            <h1 className="font-title text-left text-[clamp(34px,10.6vw,84px)] font-bold leading-[1.06] tracking-[-0.03em] text-[#FAF8F4] sm:text-[clamp(31px,6.3vw,84px)] sm:leading-[1.07] sm:tracking-[-0.035em] sm:whitespace-nowrap" data-id="element-832" data-mp="hl238z66ow8k">
              <span className="block overflow-hidden pb-[0.08em]" data-id="element-833" data-mp="2s7w3bz6xyyam">
                <motion.span initial={reduce ? false : {
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            duration: 0.9,
            delay: 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="block" data-id="element-834" data-mp="2w867r7f35gnd">
                  The AI platform
                  <br className="sm:hidden" data-id="element-835" data-mp="3fxgqkua4ohpp" />
                  <span className="hidden sm:inline" data-id="element-836" data-mp="3fxqgyiz34qvx"> </span>
                  for companies
                </motion.span>
              </span>
              {/* The switching phrase sits at the END of the line, so its two
               lengths change nothing that follows — the sentence never shifts
               under the reader as the promise swaps. */}
              <span className="block overflow-hidden pb-[0.14em]" data-id="element-837" data-mp="7w1xy5xsyks9">
                <motion.span initial={reduce ? false : {
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1
          }} transition={{
            duration: 0.9,
            delay: 0.19,
            ease: [0.16, 1, 0.3, 1]
          }} className="block" data-id="element-838" data-mp="7t5z8fe4rs2r">
                  that want to{' '}
                  {/* The clip needs room BELOW the baseline or it shaves the
                   descender of "go faster." The negative margin cancels that
                   room again so the line's own spacing is unchanged. */}
                  {/* The brand orange, not the light tint. #FB923C was chosen
                   when this line sat over a darker plate; against the current
                   hero it reads washed out and sits apart from every other
                   accent on the page. */}
                  <span className="relative -mb-[0.18em] inline-block overflow-hidden pb-[0.18em] align-bottom text-[#EA580C]" data-id="element-839" data-mp="14khzl0e6sklq">
                    <AnimatePresence mode="wait" initial={false} data-id="element-840" data-mp="33sqblm8k3oiy">
                      <motion.span key={PROMISES[index]} initial={reduce ? false : {
                  y: '105%',
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} exit={reduce ? undefined : {
                  y: '-105%',
                  opacity: 0
                }} transition={{
                  duration: 0.46,
                  ease: [0.22, 1, 0.36, 1]
                }} className="block" data-id="element-841" data-mp="33s6pdtbvidz4">
                        {PROMISES[index]}
                        <span className="text-[#FAF8F4]" data-id="element-842" data-mp="2b3swtt3k09uy">.</span>
                      </motion.span>
                    </AnimatePresence>
                  </span>
                </motion.span>
              </span>
            </h1>
          </SceneBlock>
        </div>} productHeader={<>
          {/* No eyebrow. A claim this size does not need a label announcing
           that it is a claim, and the label was pushing the block off centre
           in a frame that is now sized to the words alone. */}
          {/* Bigger than any section headline on the page, and one sentence
           long. This frame is the top of the climb with nothing else in it —
           the only thing that earns it is a claim large enough to be read as
           the reason the rest of the page exists. It names the company brain,
           which is the idea the whole page is an argument for; the sections
           below then show how it is built, staffed and governed. */}
          {/* An invitation rather than a description of the future: it hands
           the reader the verb, and "full speed" names a ceiling they have not
           seen their own company reach — which is the itch every section below
           goes on to scratch. Broken so "at full speed" lands on its own line,
           since that is the promise; "Run your company" is only the setup. */}
          <h2 className="font-title text-[clamp(34px,9.4vw,96px)] font-bold leading-[1.02] tracking-[-0.03em] text-[#FAF8F4] sm:text-[clamp(40px,7.4vw,96px)] sm:leading-[1.05] sm:tracking-[-0.035em]" data-id="element-843" data-mp="1cuigpwmbr1h">
            Run your company
            <br data-id="element-844" data-mp="g20mcr6ei5fp" />
            at full speed.
          </h2>
        </>} data-id="element-829" data-mp="u2uyytx748qi" />;
}